import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiPhone, FiMail, FiMenu, FiX, FiHome } from 'react-icons/fi';
import { buildImageUrl } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import { Container, Button } from './ui';

const Wrapper = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TopBar = styled.div`
  background: ${({ theme }) => theme.colors.neutral[50]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (max-width: 768px) {
    display: none;
  }
`;

const TopBarInner = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  height: 38px;
`;

const TopItem = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: inherit;
  transition: color 0.2s ease;

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Bar = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  height: 76px;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-width: 0;
`;

const BrandMark = styled.span`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandName = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    max-width: 180px;
  }
`;

const LogoImg = styled.img`
  height: 48px;
  max-width: 220px;
  object-fit: contain;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  padding: 0.5rem 0.9rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.neutral[700])};
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 900px) {
    display: none;
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.foreground};
  padding: 0.4rem;
  margin-left: auto;

  @media (max-width: 900px) {
    display: inline-flex;
  }
`;

const Drawer = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.background};
  }
`;

const DrawerInner = styled(Container)`
  display: flex;
  flex-direction: column;
  padding-top: 0.75rem;
  padding-bottom: 1.25rem;
`;

const DrawerLink = styled(Link)`
  padding: 0.9rem 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.foreground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const NAV = [
  { to: '/imoveis', label: 'Imóveis' },
  { to: '/mapa', label: 'Mapa' },
  { to: '/sobre', label: 'Sobre' },
  { to: '/blog', label: 'Blog' },
  { to: '/contato', label: 'Contato' },
];

const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { siteSettings } = useSettings();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const phone = siteSettings?.company_info?.phone || '';
  const email = siteSettings?.company_info?.email || '';
  const logo = siteSettings?.customization?.logo_url || '';
  const companyName =
    siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária';

  const isActive = (to: string) => location.pathname.startsWith(to);

  return (
    <Wrapper>
      <TopBar>
        <TopBarInner>
          {phone && (
            <TopItem href={`tel:${phone.replace(/\D/g, '')}`}>
              <FiPhone size={14} /> {phone}
            </TopItem>
          )}
          {email && (
            <TopItem href={`mailto:${email}`}>
              <FiMail size={14} /> {email}
            </TopItem>
          )}
        </TopBarInner>
      </TopBar>

      <Bar as="div">
        <Brand to="/">
          {logo ? (
            <LogoImg
              src={buildImageUrl(logo)}
              alt={companyName}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <>
              <BrandMark>
                <FiHome size={22} />
              </BrandMark>
              <BrandName>{companyName}</BrandName>
            </>
          )}
        </Brand>

        <Nav>
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} $active={isActive(item.to)}>
              {item.label}
            </NavLink>
          ))}
        </Nav>

        <Actions>
          <Button as={Link} to="/anunciar" $variant="primary" $size="md">
            Anunciar imóvel
          </Button>
        </Actions>

        <MenuToggle aria-label="Abrir menu" onClick={() => setOpen((v) => !v)}>
          {open ? <FiX size={26} /> : <FiMenu size={26} />}
        </MenuToggle>
      </Bar>

      <Drawer $open={open}>
        <DrawerInner>
          {NAV.map((item) => (
            <DrawerLink key={item.to} to={item.to}>
              {item.label}
            </DrawerLink>
          ))}
          <Button
            as={Link}
            to="/anunciar"
            $variant="primary"
            $full
            style={{ marginTop: '1rem' }}
          >
            Anunciar imóvel
          </Button>
          {phone && (
            <Button
              as="a"
              href={`tel:${phone.replace(/\D/g, '')}`}
              $variant="outline"
              $full
              style={{ marginTop: '0.6rem' }}
            >
              <FiPhone size={16} /> {phone}
            </Button>
          )}
        </DrawerInner>
      </Drawer>
    </Wrapper>
  );
};

export default Header;
